/**
 * Rock Off Retail - consent adapter for Shopify's Customer Privacy API
 * (rewritten 29 Sep 2026; replaces the 1 Sep theme-native banner, LAUNCH-PLAN
 * section C, "Option A": Shopify's own banner is the only banner).
 *
 * Design / UK PECR posture:
 *   - Shopify's cookie banner (Settings > Customer privacy) COLLECTS consent.
 *     This file collects nothing and shows nothing. It only reads Shopify's
 *     state and exposes it to our scripts through the same window.ROConsent
 *     seam they already use.
 *   - Nothing non-essential runs until Shopify reports it. allowed() fails
 *     CLOSED: before the API has loaded, if it errors, if it is missing, or
 *     for an unknown category, the answer is false. Internally each category
 *     is TRI-STATE (true / false / unknown): allowed() is true only for a
 *     confirmed true; unknown (missing method, throw, non-boolean, API absent)
 *     is treated as "not allowed" but is NOT a withdrawal.
 *   - The API counts as loaded only if all three methods exist as functions;
 *     otherwise it is a failed init (retry, no purge).
 *   - Category mapping:
 *       'personalisation' -> customerPrivacy.preferencesProcessingAllowed()
 *       'analytics'       -> customerPrivacy.analyticsProcessingAllowed()
 *       'marketing'       -> customerPrivacy.marketingAllowed()
 *   - When consent is CONFIRMED withdrawn (document event
 *     `visitorConsentCollected` whose field is explicitly false, or, on load,
 *     currentVisitorConsent() reporting the visitor answered 'no' - never
 *     merely unanswered, unknown or erroring) the passive
 *     tracking data already held locally is purged: preferences off -> hub
 *     view counts, viewed products and the queued Klaviyo payloads go;
 *     marketing off -> the queued Klaviyo payloads go. Explicit user-requested
 *     state (wishlist, followed bands) is functional storage and is kept.
 *   - The old theme-native consent record (localStorage ro_consent_v1) is our
 *     own leftover and is removed once on load. It is no longer read.
 *   - The API loads asynchronously (Shopify.loadFeatures, consent-tracking-api).
 *     A failed or missing load is retried up to 3 times (backoff 1s, 3s, 6s;
 *     each attempt also has a 4s watchdog). After that the state is
 *     "definitively failed" and onReady callbacks fire anyway; allowed() simply
 *     stays false (fail closed).
 *   - DELIBERATE: an init/load failure does NOT purge anything. A transient
 *     failure must never wipe a consented visitor's stored history; nothing is
 *     read or sent while allowed() is false, and the data is purged only on a
 *     real withdrawal (the API says preferences/marketing are off).
 *   - Purge never removes the whole affinity record because of a storage write
 *     error (that would lose wishlist / follows). Only an unparseable record
 *     is removed outright.
 *
 * This file MUST load (deferred) BEFORE rockoff-affinity.js in
 * layout/theme.liquid - deferred scripts execute in document order, and
 * affinity's isConsented() treats a missing window.ROConsent as "no consent"
 * (fail closed).
 *
 * Public API (window.ROConsent), unchanged shape plus the two hooks:
 *   allowed(category) -> boolean   // the seam rockoff-affinity.js / rockoff-for-you.js read
 *   get()             -> 'accepted' | 'declined' | null   // derived from Shopify's state
 *   set(choice)       -> no-op (logs a warning); Shopify owns the choice
 *   open()            -> does what a click on Shopify's own "Cookie preferences"
 *                        link does (see open() below)
 *   onReady(cb)       -> cb() once, when the API has loaded OR definitively
 *                        failed; immediately if already settled. Consumers call
 *                        allowed() inside cb (false on failure).
 *   onChange(cb)      -> cb(detail) on every visitorConsentCollected event
 */
(function () {
  'use strict';

  // Keys owned by rockoff-affinity.js - purged (surgically) on withdrawal.
  var AFFINITY_KEY = 'ro_affinity_v1';
  var KLA_QUEUE_KEY = 'ro_kla_queue_v1';
  // Retired 29 Sep 2026: our own old consent record. Removed once on load.
  var LEGACY_CONSENT_KEY = 'ro_consent_v1';
  // Same href form Shopify's own "Cookie preferences" policy link uses (and the
  // footer's standalone link): the banner script keys on the hash.
  var PREFERENCES_URL = '/policies/#shopifyReshowConsentBanner';

  // Retry schedule after a failed / missing loadFeatures (ms), and the
  // per-attempt watchdog in case loadFeatures never calls back.
  var RETRY_DELAYS = [1000, 3000, 6000];
  var ATTEMPT_TIMEOUT = 4000;

  var settled = false;
  var readyCallbacks = [];
  var changeCallbacks = [];
  var failures = 0;
  var inFlight = false;
  var retryPending = false;
  var attemptToken = 0;

  function removeKey(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      /* storage unavailable - nothing to remove */
    }
  }

  // Preferences withdrawn = personalisation stops AND its passive data goes.
  // Surgical: hub view counts + viewed products are wiped, but wishlist /
  // follows are explicit user-requested (functional) state and survive. The
  // pending Klaviyo queue is removed outright (must never send).
  function purgeTrackingData() {
    removeKey(KLA_QUEUE_KEY);
    var raw;
    try {
      raw = window.localStorage.getItem(AFFINITY_KEY);
    } catch (err) {
      return; // storage unavailable - nothing to purge
    }
    if (!raw) return;

    // Parse errors and write errors are handled separately: only a record
    // that cannot be parsed is removed outright.
    var data = null;
    var parsed = false;
    try {
      data = JSON.parse(raw);
      parsed = true;
    } catch (err) {
      parsed = false;
    }
    if (!parsed || !data || typeof data !== 'object' || Array.isArray(data)) {
      removeKey(AFFINITY_KEY);
      return;
    }

    data.hubs = {};
    data.products = [];
    try {
      window.localStorage.setItem(AFFINITY_KEY, JSON.stringify(data));
      return;
    } catch (err) {
      /* write failed - retry below with explicit state only */
    }
    // Write error: retry with a compact record holding only the explicit
    // (functional) state. NEVER remove the record because of a write error.
    var compact = {
      v: data.v,
      hubs: {},
      products: [],
      follows: Array.isArray(data.follows) ? data.follows : [],
      wishlist: Array.isArray(data.wishlist) ? data.wishlist : [],
    };
    try {
      window.localStorage.setItem(AFFINITY_KEY, JSON.stringify(compact));
    } catch (err) {
      // Still cannot write: leave the record in place (wishlist/follows are
      // kept). Nothing reads passive data while allowed() is false.
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('ROConsent: could not rewrite the affinity record during purge; left untouched.');
      }
    }
  }

  function getApi() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      return api && typeof api === 'object' ? api : null;
    } catch (err) {
      return null;
    }
  }

  // The API counts as loaded only when all three methods we depend on exist
  // as functions. A partial / half-initialised object is treated as a failed
  // init (retry, never purge).
  var REQUIRED_METHODS = ['preferencesProcessingAllowed', 'analyticsProcessingAllowed', 'marketingAllowed'];
  function apiReady() {
    var api = getApi();
    if (!api) return false;
    for (var i = 0; i < REQUIRED_METHODS.length; i++) {
      if (typeof api[REQUIRED_METHODS[i]] !== 'function') return false;
    }
    return true;
  }

  // Tri-state answer of one boolean API method:
  //   true    - the method exists and returned boolean true
  //   false   - the method exists and returned boolean false, without throwing
  //             (a CONFIRMED refusal from a working API)
  //   null    - unknown: API absent, method missing, threw, or non-boolean
  // Only `true` allows anything; only `false` may ever trigger a purge.
  function askApi(method) {
    var api = getApi();
    if (!api || typeof api[method] !== 'function') return null;
    try {
      var v = api[method]();
      return v === true ? true : v === false ? false : null;
    } catch (err) {
      return null;
    }
  }

  function categoryMethod(category) {
    switch (category) {
      case 'personalisation':
        return 'preferencesProcessingAllowed';
      case 'analytics':
        return 'analyticsProcessingAllowed';
      case 'marketing':
        return 'marketingAllowed';
      default:
        return null;
    }
  }

  // true / false / null (unknown). Unknown category is unknown.
  function stateOf(category) {
    var method = categoryMethod(category);
    return method ? askApi(method) : null;
  }

  // Fail closed: true ONLY for a confirmed true.
  function allowed(category) {
    return stateOf(category) === true;
  }

  // What the visitor actually answered, per Shopify's currentVisitorConsent():
  // 'yes' | 'no' | '' per category ('' = never answered). Returns the string
  // for `field` ('preferences' | 'marketing'), or null when the method is
  // missing, throws, or the field is not a string (unknown).
  function answeredValue(field) {
    var api = getApi();
    if (!api || typeof api.currentVisitorConsent !== 'function') return null;
    try {
      var c = api.currentVisitorConsent();
      if (c && typeof c === 'object' && typeof c[field] === 'string') return c[field];
    } catch (err) {
      /* unknown */
    }
    return null;
  }

  // Apply withdrawal side effects. A withdrawal is CONFIRMED only when:
  //   - the visitorConsentCollected event carries the field as an explicit
  //     boolean false, or
  //   - (event field absent, or the on-load call with detail null)
  //     currentVisitorConsent() says the visitor answered 'no'.
  // A visitor who has NOT answered (allowed() false, currentVisitorConsent()
  // fields all '') is not a withdrawal, e.g. Shopify's consent cookie was lost
  // while localStorage survived. Missing / throwing currentVisitorConsent()
  // is unknown and never purges.
  function applyWithdrawals(detail) {
    var prefsOff = detail && typeof detail.preferencesAllowed === 'boolean' ? detail.preferencesAllowed === false : answeredValue('preferences') === 'no';
    var marketingOff = detail && typeof detail.marketingAllowed === 'boolean' ? detail.marketingAllowed === false : answeredValue('marketing') === 'no';
    if (prefsOff) purgeTrackingData();
    if (marketingOff) removeKey(KLA_QUEUE_KEY);
  }

  function get() {
    var api = getApi();
    if (!api) return null;
    try {
      // currentVisitorConsent() returns 'yes' | 'no' | '' per category;
      // all empty = the visitor has not chosen yet.
      if (typeof api.currentVisitorConsent === 'function') {
        var c = api.currentVisitorConsent();
        if (c && typeof c === 'object') {
          var chosen = false;
          for (var k in c) {
            if (Object.prototype.hasOwnProperty.call(c, k) && (c[k] === 'yes' || c[k] === 'no')) chosen = true;
          }
          if (!chosen) return null;
        }
      }
    } catch (err) {
      return null;
    }
    var p = stateOf('personalisation');
    return p === true ? 'accepted' : p === false ? 'declined' : null;
  }

  function set() {
    if (window.console && typeof window.console.warn === 'function') {
      window.console.warn("ROConsent.set() is a no-op: consent is collected by Shopify's cookie banner.");
    }
  }

  // Kept for API compatibility. Shopify's banner script re-opens the banner
  // when a link with the #shopifyReshowConsentBanner hash is clicked, so this
  // does exactly that: it clicks a temporary anchor with the same href form
  // the footer uses ('/policies/#shopifyReshowConsentBanner'). No dialog method
  // is assumed on the Customer Privacy API.
  function open() {
    try {
      var a = document.createElement('a');
      a.setAttribute('href', PREFERENCES_URL);
      a.style.display = 'none';
      var host = document.body || document.documentElement;
      if (host) host.appendChild(a);
      a.click();
      if (host && a.parentNode) a.parentNode.removeChild(a);
    } catch (err) {
      try {
        window.location.assign(PREFERENCES_URL);
      } catch (err2) {
        /* never throw */
      }
    }
  }

  function runCallbacks(list, arg) {
    var copy = list.slice();
    for (var i = 0; i < copy.length; i++) {
      try {
        copy[i](arg);
      } catch (err) {
        /* one consumer's error must never break the others */
      }
    }
  }

  // Settle exactly once: the API has loaded, or it has definitively failed.
  function settle() {
    if (settled) return;
    settled = true;
    var cbs = readyCallbacks;
    readyCallbacks = [];
    runCallbacks(cbs);
  }

  function onReady(cb) {
    if (typeof cb !== 'function') return;
    if (settled) {
      try {
        cb();
      } catch (err) {
        /* swallow */
      }
      return;
    }
    readyCallbacks.push(cb);
  }

  function onChange(cb) {
    if (typeof cb === 'function') changeCallbacks.push(cb);
  }

  window.ROConsent = {
    allowed: allowed,
    get: get,
    set: set,
    open: open,
    onReady: onReady,
    onChange: onChange,
  };

  // Register immediately so an early consent event is never missed.
  document.addEventListener('visitorConsentCollected', function (evt) {
    var detail = evt && evt.detail;
    applyWithdrawals(detail);
    // The event can only fire once the API exists.
    if (apiReady()) settle();
    runCallbacks(changeCallbacks, detail);
  });

  function apiLoaded() {
    // Check on load: consent withdrawn on a previous visit.
    applyWithdrawals(null);
    settle();
  }

  // One failed / missing attempt: retry with backoff, or give up (settle)
  // after the last retry. Giving up purges nothing; allowed() stays false.
  function failAttempt() {
    if (settled) return;
    if (failures < RETRY_DELAYS.length) {
      var delay = RETRY_DELAYS[failures];
      failures++;
      retryPending = true;
      setTimeout(function () {
        retryPending = false;
        requestApi();
      }, delay);
    } else {
      settle();
    }
  }

  function requestApi() {
    if (settled || inFlight || retryPending) return;
    if (apiReady()) {
      apiLoaded();
      return;
    }
    var canLoad = false;
    try {
      canLoad = !!(window.Shopify && typeof window.Shopify.loadFeatures === 'function');
    } catch (err) {
      canLoad = false;
    }
    if (!canLoad) {
      failAttempt();
      return;
    }
    inFlight = true;
    var token = ++attemptToken;
    var watchdog = setTimeout(function () {
      if (token !== attemptToken || !inFlight) return;
      inFlight = false;
      attemptToken++;
      failAttempt();
    }, ATTEMPT_TIMEOUT);
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (token !== attemptToken || !inFlight) return;
        inFlight = false;
        clearTimeout(watchdog);
        if (error || !apiReady()) {
          failAttempt();
          return;
        }
        apiLoaded();
      });
    } catch (err) {
      if (token === attemptToken && inFlight) {
        inFlight = false;
        clearTimeout(watchdog);
        failAttempt();
      }
    }
  }

  removeKey(LEGACY_CONSENT_KEY);
  requestApi();
  // Shopify's own script may attach loadFeatures after this deferred file
  // runs on a slow load; try again when the window has finished loading
  // (a no-op while a retry is already scheduled or in flight).
  window.addEventListener('load', requestApi);
})();
